import React from "react";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import {formatNumber} from "../../modules/common/utils/number";
import {
    BuildingStorefrontIcon,
    DocumentTextIcon,
    ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import {ItemRow, TotalRow} from "../../modules/payment/components/SummaryComponents";

interface CheckoutPriceSummaryProps {
    totalWithoutTaxes: number;
    taxes: number;
    taxRate?: number;
    discount?: number;
    discountAmount?: number;
    discountPercent?: number;
    totalBasket: number;
    currencySymbol: string;
}

const CheckoutPriceSummary: React.FC<CheckoutPriceSummaryProps> = ({
    totalWithoutTaxes,
    taxes,
    taxRate,
    discount,
    discountAmount,
    discountPercent,
    totalBasket,
    currencySymbol,
}) => {
    const t = useTranslate();

    return (
        <div className="max-w-full space-y-4">
            <h3 className="text-2xl font-semibold mb-2">{t('bookOk.summaryTitle')}</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">{t('bookOk.summarySubtitle')}</p>

            {/* ── Desglose: subtotal + impuestos + descuento + total ── */}
            <div className="bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">

                {/* Subtotal sin impuestos */}
                <div className="px-4">
                    <ItemRow
                        icon={<BuildingStorefrontIcon className="w-6 h-6 text-indigo-500" />}
                        iconBg="bg-indigo-50 dark:bg-indigo-900/20"
                        title={t('checkout.total_without_taxes')}
                        subtitle={t('bookOk.totalRoomsSubtitle')}
                        amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{formatNumber(totalWithoutTaxes)} {currencySymbol}</span>}
                    />
                </div>

                {/* Impuestos */}
                <div className="px-4">
                    <ItemRow
                        icon={<ReceiptPercentIcon className="w-6 h-6 text-purple-500" />}
                        iconBg="bg-purple-50 dark:bg-purple-900/20"
                        title={t('checkout.total_taxes') + ` (${taxRate}%)`}
                        subtitle={t('bookOk.taxesSubtitle')}
                        amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{formatNumber(taxes)} {currencySymbol}</span>}
                    />
                </div>

                {/* Descuento (si existe) */}
                {discountAmount && discountAmount > 0 && (
                    <div className="px-4">
                        <ItemRow
                            icon={<DocumentTextIcon className="w-6 h-6 text-red-500" />}
                            iconBg="bg-red-50 dark:bg-red-900/20"
                            title={t('checkout.discount') + (discountPercent ? ` (${discount}%)` : '')}
                            subtitle={t('checkout.discount')}
                            amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">−{formatNumber(discountAmount)} {currencySymbol}</span>}
                        />
                    </div>
                )}

                {/* Total de la reserva */}
                <TotalRow
                    label={t('checkout.total')}
                    amount={<>{formatNumber(totalBasket)} {currencySymbol}</>}
                />
            </div>
        </div>
    );
};

export default CheckoutPriceSummary;
